// Operations page — read-only stats dashboard.
//
// Spec: TKOS_MASTER_CONTEXT.md §Operations + inventory_sections/01_retailer.md.
// Source-of-truth render lives in portal-standalone.html ~7088 (`renderOperations`).
//
// Data sources
// ------------
// The retailer-facing kitchenos-api currently exposes NO `/api/portal/...`
// endpoints for installs, surveys, snags, in-transit deliveries, or awaiting
// invoices (verified 2026-05 against /var/www/kitchenos-api/server.js).
// Until those endpoints land, this page calls a single placeholder endpoint
// (`/api/portal/operations/stats`) — if the backend returns 404, we fall back
// to the same realistic demo shape the source-of-truth used. See `loadStats`.
//
// Recent Activity feed
// --------------------
// The inventory flags this section as MISSING in the legacy code (it was on
// Dashboard only). We implement it fresh here, deriving events from the same
// stats payload (overdue invoices, delayed deliveries, completed installs,
// raised snags). When real endpoints arrive, swap `deriveActivity` for a
// dedicated feed source.
//
// Auto-refresh
// ------------
// Every 60s the page re-fetches stats in the background and re-renders.
// Cleanup tears down the interval and aborts any in-flight fetch.

import { ApiError } from "@tkos/api-client";
import { iconMarkup, type IconName } from "@tkos/shared-ui";
import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

interface InstallRow {
  customer: string;
  where: string;
  date: string;
  team: string;
  value: number;
  progress: number;
  note: string;
}

interface SurveyRow {
  customer: string;
  where: string;
  date: string;
  surveyor: string;
  status: "confirmed" | "pending";
}

interface SnagRow {
  customer: string;
  issue: string;
  raised: string;
  priority: "high" | "medium" | "low";
  owner: string;
}

interface DeliveryRow {
  ref: string;
  customer: string;
  eta: string;
  supplier: string;
  status: "Scheduled" | "Out for delivery" | "Delivered" | "Delayed";
}

interface AwaitingRow {
  customer: string;
  invoice: string;
  amount: number;
  /** Free-text e.g. "Overdue 3d" or "Due in 5d". Anything starting with "Overdue" is past-due. */
  dueIn: string;
}

interface ActivityEvent {
  icon: string;
  text: string;
  /** ISO timestamp, used to render relative time. */
  at: string;
  tone: "ok" | "info" | "warn" | "danger" | "money";
}

interface OpsStats {
  installs: InstallRow[];
  surveys: SurveyRow[];
  snags: SnagRow[];
  inTransit: DeliveryRow[];
  awaiting: AwaitingRow[];
  /** "Last 30 days · jobs on time" percentage. */
  onTimePct: number;
  industryAvgPct: number;
  /** Optional dedicated activity feed; if absent we derive from the rest. */
  activity?: ActivityEvent[];
}

// ── Formatters ───────────────────────────────────────────────────────────────

const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

const RTF = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });

function relTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diffSec = Math.round((t - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return RTF.format(diffSec, "second");
  if (abs < 3600) return RTF.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return RTF.format(Math.round(diffSec / 3600), "hour");
  if (abs < 86400 * 30) return RTF.format(Math.round(diffSec / 86400), "day");
  if (abs < 86400 * 365) return RTF.format(Math.round(diffSec / (86400 * 30)), "month");
  return RTF.format(Math.round(diffSec / (86400 * 365)), "year");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ── Empty fallback ───────────────────────────────────────────────────────────
// Until the retailer-facing operations endpoints land, the page renders an
// empty/zero state rather than demo figures. Every list shows its own
// empty-state message and the cards read 0.

function emptyStats(): OpsStats {
  return {
    installs: [],
    surveys: [],
    snags: [],
    inTransit: [],
    awaiting: [],
    onTimePct: 0,
    industryAvgPct: 0,
    activity: [],
  };
}

// ── Activity derivation (when backend doesn't supply one) ────────────────────

function deriveActivity(s: OpsStats): ActivityEvent[] {
  if (s.activity && s.activity.length > 0) return s.activity;
  const now = Date.now();
  const events: ActivityEvent[] = [];

  for (const i of s.installs) {
    if (i.progress === 100) {
      events.push({
        icon: "✅",
        text: `${i.customer} install completed — ${GBP.format(i.value)} sign-off due`,
        at: new Date(now - 30 * 60_000).toISOString(),
        tone: "ok",
      });
    }
  }
  for (const d of s.inTransit) {
    if (d.status === "Delayed") {
      events.push({
        icon: "📦",
        text: `${d.supplier} delayed: ${d.ref} — ETA ${d.eta}`,
        at: new Date(now - 2 * 3_600_000).toISOString(),
        tone: "danger",
      });
    }
  }
  for (const a of s.awaiting) {
    if (/^Overdue/i.test(a.dueIn)) {
      events.push({
        icon: "💰",
        text: `Invoice ${a.invoice} ${a.dueIn.toLowerCase()} — ${GBP.format(a.amount)}`,
        at: new Date(now - 26 * 3_600_000).toISOString(),
        tone: "money",
      });
    }
  }
  for (const sn of s.snags) {
    if (sn.priority === "high") {
      events.push({
        icon: "🔧",
        text: `Snag raised: ${sn.customer} — ${sn.issue}`,
        at: new Date(now - 30 * 3_600_000).toISOString(),
        tone: "warn",
      });
    }
  }

  return events.slice(0, 8);
}

// ── Backend fetch ────────────────────────────────────────────────────────────

async function loadStats(
  ctx: PageContext,
  signal: AbortSignal,
): Promise<{ data: OpsStats; usedFallback: boolean }> {
  // TODO: when retailer-facing endpoints land, replace this with a fan-out:
  //   const [installs, surveys, snags, inTransit, awaiting] = await Promise.all([
  //     ctx.api<InstallRow[]>("/portal/installations?window=week", { signal }),
  //     ctx.api<SurveyRow[]>("/portal/surveys?status=upcoming",     { signal }),
  //     ctx.api<SnagRow[]>("/portal/snags?status=open",             { signal }),
  //     ctx.api<DeliveryRow[]>("/portal/deliveries?status=in-transit", { signal }),
  //     ctx.api<AwaitingRow[]>("/portal/payments/awaiting",         { signal }),
  //   ]);
  // None of these exist on kitchenos-api at time of writing.
  try {
    const data = await ctx.api<OpsStats>("/portal/operations/stats", { signal });
    return { data, usedFallback: false };
  } catch (err) {
    if (signal.aborted) throw err;
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      return { data: emptyStats(), usedFallback: true };
    }
    // Network / 500 — surface the demo data so the page still renders, but
    // let the caller toast about it.
    throw err;
  }
}

// ── Card spec ────────────────────────────────────────────────────────────────

interface CardSpec {
  key: CardKey;
  label: string;
  /** Icon from the shared-ui set; rendered via iconMarkup() in renderCards. */
  icon: IconName;
  color: string;
  bg: string;
  value: string;
  sub: string;
  subTone: "muted" | "alert" | "ok";
}

type CardKey = "installs" | "surveys" | "snags" | "deliv" | "awaiting" | "onTime";

function buildCards(s: OpsStats): CardSpec[] {
  const installNames = s.installs.map((i) => i.customer.split(" ")[0]).join(", ");
  const surveysPending = s.surveys.filter((x) => x.status === "pending").length;
  const snagsHigh = s.snags.filter((x) => x.priority === "high").length;
  const delivDelayed = s.inTransit.filter((x) => x.status === "Delayed").length;
  const overdueInvoices = s.awaiting.filter((x) => /^Overdue/i.test(x.dueIn));
  const overdueTotal = overdueInvoices.reduce((sum, a) => sum + a.amount, 0);

  // Icon mapping per Steve's spec (2026-05-19): replace the emoji set with
  // the in-house TKOS SVG icons. Stroke / colour treatment is handled by
  // .ops-card__icon — currentColor on the SVG, tinted with --ops-card-color
  // so every card picks up the right brand colour without per-icon CSS.
  return [
    {
      key: "installs",
      label: "Installs This Week",
      icon: "hammer",
      color: "#e58200",
      bg: "rgba(245,158,11,0.12)",
      value: String(s.installs.length),
      sub: installNames || "No installs scheduled",
      subTone: "muted",
    },
    {
      key: "surveys",
      label: "Site Surveys",
      icon: "pin",
      color: "#3b82f6",
      bg: "rgba(59,130,246,0.12)",
      value: String(s.surveys.length),
      sub: `${surveysPending} awaiting confirmation`,
      subTone: surveysPending > 0 ? "alert" : "ok",
    },
    {
      key: "snags",
      label: "Snags Open",
      icon: "alert-triangle",
      color: "#ef4444",
      bg: "rgba(239,68,68,0.12)",
      value: String(s.snags.length),
      sub: `${snagsHigh} high priority`,
      subTone: snagsHigh > 0 ? "alert" : "ok",
    },
    {
      key: "deliv",
      label: "Delivery In Transit",
      icon: "truck",
      color: "#f59e0b",
      bg: "rgba(245,158,11,0.12)",
      value: String(s.inTransit.length),
      sub: `${delivDelayed} delayed`,
      subTone: delivDelayed > 0 ? "alert" : "ok",
    },
    {
      key: "awaiting",
      label: "Awaiting Payment",
      icon: "pound",
      color: "#a855f7",
      bg: "rgba(168,85,247,0.12)",
      value: GBP.format(overdueTotal),
      sub: `${overdueInvoices.length} invoices overdue`,
      subTone: overdueInvoices.length > 0 ? "alert" : "ok",
    },
    {
      key: "onTime",
      label: "Jobs On Time",
      icon: "chart",
      color: "#22c55e",
      bg: "rgba(34,197,94,0.12)",
      value: `${s.onTimePct}%`,
      sub: `Last 30 days · industry avg ${s.industryAvgPct}%`,
      subTone: s.onTimePct >= s.industryAvgPct ? "ok" : "alert",
    },
  ];
}

// ── Rendering ────────────────────────────────────────────────────────────────

function feedToneBg(tone: ActivityEvent["tone"]): string {
  switch (tone) {
    case "ok":
      return "rgba(34,197,94,0.14)";
    case "warn":
      return "rgba(245,158,11,0.14)";
    case "danger":
      return "rgba(239,68,68,0.14)";
    case "money":
      return "rgba(168,85,247,0.14)";
    case "info":
    default:
      return "rgba(59,130,246,0.14)";
  }
}

function renderShell(container: HTMLElement): {
  cardsEl: HTMLElement;
  detailEl: HTMLElement;
  feedEl: HTMLElement;
  bannerEl: HTMLElement;
  refreshBtn: HTMLButtonElement;
  metaEl: HTMLElement;
} {
  container.innerHTML = `
    <section class="ops-root" aria-label="Operations dashboard">
      <header class="ops-header">
        <div class="ops-header__titles">
          <h1 class="ops-title">Operations</h1>
          <p class="ops-subtitle">Installs, site surveys, snags and project tracking</p>
        </div>
        <div class="ops-header__meta" data-role="meta">
          <span class="ops-header__meta-dot" aria-hidden="true"></span>
          <span data-role="meta-text">Loading…</span>
          <button class="ops-header__refresh" type="button" data-role="refresh">Refresh</button>
        </div>
      </header>
      <div class="ops-banner" data-role="banner" hidden></div>
      <div class="ops-cards" data-role="cards" role="list"></div>
      <div class="ops-detail" data-role="detail" hidden></div>
      <section class="ops-feed" aria-labelledby="ops-feed-title">
        <div class="ops-feed__header">
          <span id="ops-feed-title">Recent Activity</span>
          <span class="ops-feed__header-note">Real-time operational events</span>
        </div>
        <ul class="ops-feed__list" data-role="feed"></ul>
      </section>
    </section>
  `;

  const cardsEl = container.querySelector<HTMLElement>("[data-role='cards']")!;
  const detailEl = container.querySelector<HTMLElement>("[data-role='detail']")!;
  const feedEl = container.querySelector<HTMLElement>("[data-role='feed']")!;
  const bannerEl = container.querySelector<HTMLElement>("[data-role='banner']")!;
  const refreshBtn = container.querySelector<HTMLButtonElement>("[data-role='refresh']")!;
  const metaEl = container.querySelector<HTMLElement>("[data-role='meta-text']")!;

  return { cardsEl, detailEl, feedEl, bannerEl, refreshBtn, metaEl };
}

function renderCards(host: HTMLElement, stats: OpsStats, openKey: CardKey | null): void {
  const cards = buildCards(stats);
  host.innerHTML = cards
    .map((c) => {
      const subClass =
        c.subTone === "alert"
          ? "ops-card__sub ops-card__sub--alert"
          : c.subTone === "ok"
          ? "ops-card__sub ops-card__sub--ok"
          : "ops-card__sub";
      const activeCls = openKey === c.key ? " ops-card--active" : "";
      return `
        <button type="button" class="ops-card${activeCls}" role="listitem"
          data-key="${escapeHtml(c.key)}"
          aria-expanded="${openKey === c.key ? "true" : "false"}"
          style="--ops-card-color:${c.color};--ops-card-bg:${c.bg}">
          <span class="ops-card__top">
            <span class="ops-card__icon-wrap">
              <span class="ops-card__icon" aria-hidden="true">${iconMarkup(c.icon, { size: "sm" })}</span>
              <span class="ops-card__label">${escapeHtml(c.label)}</span>
            </span>
            <span class="ops-card__chev" aria-hidden="true">${iconMarkup("chevron-down", { size: "xs" })}</span>
          </span>
          <span class="ops-card__value">${escapeHtml(c.value)}</span>
          <span class="${subClass}">${escapeHtml(c.sub)}</span>
        </button>
      `;
    })
    .join("");
}

// ── Detail panels — verbatim port of source-of-truth opsTogglePanel
//    (portal-standalone.html lines 7113–7164). Each function takes the
//    stats payload and returns the inner HTML for that card's panel.
//    Colour tokens use `--ops-card-color` so the panel header inherits
//    the same brand colour as the card it expands from.

/**
 * Wrap a customer name so the shell-level mini-CRM delegation picks it
 * up. The retailer-portal shell installs `installMiniCrmDelegation`
 * once on the document (shell.ts:210) — it intercepts capture-phase
 * clicks on any element with `data-customer-name` and opens the
 * popup. We just emit the attribute and a class for the hover
 * affordance; no per-page handler is needed.
 *
 * Empty names render as plain text — the mini-CRM lookup keys by
 * name and we don't want clicks on a blank pill.
 */
function customerNameSpan(name: string): string {
  const safe = escapeHtml(name);
  if (!name) return safe;
  return `<span class="ops-customer-link" data-customer-name="${safe}" tabindex="0" role="button">${safe}</span>`;
}

function panelHeader(title: string, colour: string): string {
  // Background uses the same colour-with-alpha pattern as the source —
  // a translucent tint of the card colour.
  return `
    <div class="ops-detail__head" style="--ops-card-color:${colour}">
      <div class="ops-detail__title">${escapeHtml(title)}</div>
      <button type="button" class="ops-detail__close" data-action="close-detail">Close</button>
    </div>
  `;
}

function detailInstalls(s: OpsStats): string {
  const total = s.installs.reduce((sum, i) => sum + i.value, 0);
  const colour = "#e58200";
  const rows = s.installs
    .map((i) => {
      const col =
        i.progress === 100 ? "#22c55e"
        : i.progress >= 70 ? "#e58200"
        : i.progress >= 40 ? "#f59e0b"
        : "#3b82f6";
      return `
        <div class="ops-detail__row">
          <div class="ops-detail__row-head">
            <div class="ops-detail__row-name">${customerNameSpan(i.customer)} <span class="ops-detail__row-sub">${escapeHtml(i.where)}</span></div>
            <div class="ops-detail__row-amount">${escapeHtml(GBP.format(i.value))}</div>
          </div>
          <div class="ops-detail__row-meta">${escapeHtml(i.team)} · ${escapeHtml(i.date)}</div>
          <div class="ops-detail__progress">
            <div class="ops-detail__progress-track">
              <div class="ops-detail__progress-fill" style="width:${i.progress}%;background:${col}"></div>
            </div>
            <span class="ops-detail__progress-pct" style="color:${col}">${i.progress}%</span>
          </div>
          <div class="ops-detail__row-note">${escapeHtml(i.note)}</div>
        </div>
      `;
    })
    .join("");
  return (
    panelHeader(`Installs this week · total ${GBP.format(total)}`, colour) +
    `<div class="ops-detail__body">${rows || emptyRow("No installs scheduled.")}</div>`
  );
}

function detailSurveys(s: OpsStats): string {
  const colour = "#3b82f6";
  const rows = s.surveys
    .map((sv) => {
      const col = sv.status === "confirmed" ? "#22c55e" : "#f59e0b";
      return `
        <div class="ops-detail__row ops-detail__row--inline">
          <div>
            <div class="ops-detail__row-name">${customerNameSpan(sv.customer)}</div>
            <div class="ops-detail__row-meta">${escapeHtml(sv.where)} · ${escapeHtml(sv.date)} · ${escapeHtml(sv.surveyor)}</div>
          </div>
          <span class="ops-detail__pill" style="--pill:${col}">${escapeHtml(sv.status)}</span>
        </div>
      `;
    })
    .join("");
  return (
    panelHeader("Upcoming site surveys", colour) +
    `<div class="ops-detail__body">${rows || emptyRow("No site surveys scheduled.")}</div>`
  );
}

function detailSnags(s: OpsStats): string {
  const colour = "#ef4444";
  const rows = s.snags
    .map((sn) => {
      const col = sn.priority === "high" ? "#ef4444" : "#f59e0b";
      return `
        <div class="ops-detail__row">
          <div class="ops-detail__row-head">
            <div class="ops-detail__row-name">${customerNameSpan(sn.customer)}</div>
            <span class="ops-detail__pill" style="--pill:${col}">${escapeHtml(sn.priority)}</span>
          </div>
          <div class="ops-detail__row-meta ops-detail__row-meta--strong">${escapeHtml(sn.issue)}</div>
          <div class="ops-detail__row-meta">Raised ${escapeHtml(sn.raised)} · Owner ${escapeHtml(sn.owner)}</div>
        </div>
      `;
    })
    .join("");
  return (
    panelHeader("Open snags", colour) +
    `<div class="ops-detail__body">${rows || emptyRow("No open snags — well done.")}</div>`
  );
}

function detailDeliv(s: OpsStats): string {
  const colour = "#f59e0b";
  const rows = s.inTransit
    .map((d) => {
      const col =
        d.status === "Delayed" ? "#ef4444"
        : d.status === "Out for delivery" ? "#e58200"
        : "#3b82f6";
      return `
        <div class="ops-detail__row ops-detail__row--inline">
          <div>
            <div class="ops-detail__row-name">${escapeHtml(d.ref)} · ${customerNameSpan(d.customer)}</div>
            <div class="ops-detail__row-meta">ETA ${escapeHtml(d.eta)} · ${escapeHtml(d.supplier)}</div>
          </div>
          <span class="ops-detail__pill" style="--pill:${col}">${escapeHtml(d.status)}</span>
        </div>
      `;
    })
    .join("");
  return (
    panelHeader("In transit", colour) +
    `<div class="ops-detail__body">${rows || emptyRow("Nothing in transit right now.")}</div>`
  );
}

function detailAwaiting(s: OpsStats): string {
  const colour = "#a855f7";
  const rows = s.awaiting
    .map((a) => {
      const overdue = /^Overdue/i.test(a.dueIn);
      const col = overdue ? "#ef4444" : "#22c55e";
      return `
        <div class="ops-detail__row ops-detail__row--inline">
          <div>
            <div class="ops-detail__row-name">${customerNameSpan(a.customer)} · <span class="ops-detail__row-sub">${escapeHtml(a.invoice)}</span></div>
            <div class="ops-detail__row-meta" style="color:${col};font-weight:700">${escapeHtml(a.dueIn)}</div>
          </div>
          <div class="ops-detail__row-amount">${escapeHtml(GBP.format(a.amount))}</div>
        </div>
      `;
    })
    .join("");
  return (
    panelHeader("Awaiting payment", colour) +
    `<div class="ops-detail__body">${rows || emptyRow("All paid up.")}</div>`
  );
}

function detailOnTime(s: OpsStats): string {
  const colour = "#22c55e";
  // No on-time trend data until a real trend endpoint lands — render an empty
  // chart rather than a hardcoded demo series.
  const trend: number[] = [];
  const labels = ["W-7", "W-6", "W-5", "W-4", "W-3", "W-2", "W-1", "This"];
  const bars = trend
    .map((p, i) => {
      const h = Math.round(p * 0.9);
      const lbl = labels[i] ?? "";
      return `
        <div class="ops-detail__bar">
          <div class="ops-detail__bar-pct">${p}%</div>
          <div class="ops-detail__bar-fill" style="height:${h}%"></div>
          <div class="ops-detail__bar-label">${escapeHtml(lbl)}</div>
        </div>
      `;
    })
    .join("");
  return (
    panelHeader(`On-time performance · last 8 weeks (current ${s.onTimePct}%)`, colour) +
    `<div class="ops-detail__bars">${bars}</div>`
  );
}

function emptyRow(text: string): string {
  return `<div class="ops-detail__empty">${escapeHtml(text)}</div>`;
}

function renderDetail(host: HTMLElement, stats: OpsStats, openKey: CardKey | null): void {
  if (!openKey) {
    host.hidden = true;
    host.innerHTML = "";
    return;
  }
  let body = "";
  switch (openKey) {
    case "installs": body = detailInstalls(stats); break;
    case "surveys":  body = detailSurveys(stats);  break;
    case "snags":    body = detailSnags(stats);    break;
    case "deliv":    body = detailDeliv(stats);    break;
    case "awaiting": body = detailAwaiting(stats); break;
    case "onTime":   body = detailOnTime(stats);   break;
  }
  host.innerHTML = body;
  host.hidden = false;
}

function renderFeed(host: HTMLElement, stats: OpsStats): void {
  const events = deriveActivity(stats);
  if (events.length === 0) {
    host.innerHTML = `<li class="ops-feed__empty">No recent activity.</li>`;
    return;
  }
  host.innerHTML = events
    .map(
      (e) => `
        <li class="ops-feed__item">
          <span class="ops-feed__icon" aria-hidden="true"
            style="--ops-feed-bg:${feedToneBg(e.tone)}">${e.icon}</span>
          <div class="ops-feed__body">
            <div class="ops-feed__text">${escapeHtml(e.text)}</div>
            <div class="ops-feed__when">${escapeHtml(relTime(e.at))}</div>
          </div>
        </li>
      `,
    )
    .join("");
}

// ── Page ─────────────────────────────────────────────────────────────────────

const REFRESH_MS = 60_000;

const operationsPage: Page = {
  name: "operations",
  label: "Operations",
  icon: "🛠️",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const { cardsEl, detailEl, feedEl, bannerEl, refreshBtn, metaEl } = renderShell(container);

    let controller: AbortController | null = null;
    let timer: number | null = null;
    let disposed = false;
    // Which card's detail panel is currently expanded. `null` means none.
    // Only one card can be open at a time (source-of-truth behaviour).
    let openKey: CardKey | null = null;
    // Latest stats payload — kept in scope so the click handler can re-
    // render the detail panel without re-fetching.
    let lastStats: OpsStats | null = null;

    // Card click → toggle / switch the open panel. Same key clicked twice
    // collapses; a different key swaps. Bound once (delegated) so the
    // handler survives every renderCards() re-render.
    cardsEl.addEventListener("click", (e) => {
      const target = e.target as HTMLElement | null;
      const btn = target?.closest<HTMLElement>(".ops-card");
      if (!btn) return;
      const key = btn.dataset["key"] as CardKey | undefined;
      if (!key || !lastStats) return;
      openKey = openKey === key ? null : key;
      renderCards(cardsEl, lastStats, openKey);
      renderDetail(detailEl, lastStats, openKey);
      // Bring the newly-opened panel into view (skip when collapsing).
      if (openKey) {
        detailEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    });

    // Close button inside the expanded panel.
    detailEl.addEventListener("click", (e) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest("[data-action='close-detail']")) {
        openKey = null;
        if (lastStats) {
          renderCards(cardsEl, lastStats, openKey);
        }
        renderDetail(detailEl, lastStats ?? emptyStats(), openKey);
      }
    });

    const setBanner = (msg: string | null, kind: "info" | "error" = "info"): void => {
      if (!msg) {
        bannerEl.hidden = true;
        bannerEl.textContent = "";
        bannerEl.classList.remove("ops-banner--error");
        return;
      }
      bannerEl.hidden = false;
      bannerEl.textContent = msg;
      bannerEl.classList.toggle("ops-banner--error", kind === "error");
    };

    const setMeta = (text: string): void => {
      metaEl.textContent = text;
    };

    const refresh = async (announce: boolean): Promise<void> => {
      if (disposed) return;

      // Abort any prior in-flight fetch and start a fresh one.
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;

      refreshBtn.disabled = true;
      cardsEl.classList.add("is-loading");

      try {
        const { data, usedFallback } = await loadStats(ctx, signal);
        if (disposed || signal.aborted) return;

        lastStats = data;
        renderCards(cardsEl, data, openKey);
        renderDetail(detailEl, data, openKey);
        renderFeed(feedEl, data);

        const updated = new Date();
        setMeta(`Updated ${updated.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`);

        if (usedFallback) {
          setBanner(
            "Live operations data not available yet — backend endpoints pending.",
            "info",
          );
        } else {
          setBanner(null);
        }

        if (announce) {
          ctx.showToast("Operations refreshed", "success");
        }
      } catch (err) {
        if (signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Couldn't load operations (HTTP ${err.status})`
            : err instanceof Error
            ? err.message
            : "Couldn't load operations";
        setBanner(`${msg}.`, "error");
        // Render the empty state so the page still mounts.
        const fallback = emptyStats();
        lastStats = fallback;
        renderCards(cardsEl, fallback, openKey);
        renderDetail(detailEl, fallback, openKey);
        renderFeed(feedEl, fallback);
        if (announce) ctx.showToast(msg, "error");
      } finally {
        if (!disposed) {
          refreshBtn.disabled = false;
          cardsEl.classList.remove("is-loading");
        }
      }
    };

    refreshBtn.addEventListener("click", () => {
      void refresh(true);
    });

    // Initial load.
    void refresh(false);

    // Auto-refresh every 60s.
    timer = window.setInterval(() => {
      void refresh(false);
    }, REFRESH_MS);

    return (): void => {
      disposed = true;
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
      controller?.abort();
      controller = null;
    };
  },
};

registerPage(operationsPage);

export default operationsPage;
